import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';

// Components
import Navbar from './components/Navbar';
import Auth from './components/Auth';
import CustomerAuth from './components/CustomerAuth';
import UserTypeSelection from './components/UserTypeSelection';
import LandingPage from './components/LandingPage';
import Dashboard from './pages/Dashboard';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerPortalComingSoon from './pages/CustomerPortalComingSoon';
import Dealers from './pages/Dealers';
import Purchases from './pages/Purchases';
import Sales from './pages/Sales';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import Profit from './pages/Profit';
import Expenses from './pages/Expenses';
import Invoices from './pages/Invoices';
import Profile from './pages/Profile';
import CustomerProfile from './pages/CustomerProfile';
import Settings from './pages/Settings';
import BusinessIntelligence from './pages/BusinessIntelligence';
import ReportsOverview from './pages/ReportsOverview';
import DataImport from './pages/DataImport';
import ProductCatalog from './pages/ProductCatalog';
import ShoppingCart from './pages/ShoppingCart';
import Checkout from './pages/Checkout';
import StoreLocator from './pages/StoreLocator';
import PointsHistory from './pages/PointsHistory';
import Orders from './pages/Orders';
import EcommerceInterest from './pages/EcommerceInterest';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DatabaseTest from './pages/DatabaseTest';

// React Router
import { Link, useLocation } from 'react-router-dom';

// Material-UI Components
import { Avatar, Button, Typography } from '@mui/material';

// API

const normalizeBusinessData = (business = {}) => {
  if (!business || typeof business !== 'object') {
    return null;
  }

  const normalizedName = business.name || business.companyName || business.businessName || '';
  const normalizedType = business.type || business.businessType || business.business_type || '';

  return {
    ...business,
    id: business.id || business.companyId || business.company_id || null,
    name: normalizedName,
    companyName: business.companyName || normalizedName,
    type: normalizedType,
    businessType: business.businessType || normalizedType,
    industry: business.industry || '',
    address: business.address || '',
    city: business.city || '',
    state: business.state || '',
    zipCode: business.zipCode || business.zip_code || '',
    country: business.country || 'India',
    phone: business.phone || business.businessPhone || '',
    website: business.website || '',
    description: business.description || '',
    employeeCount: business.employeeCount ?? business.employee_count ?? '',
    foundedYear: business.foundedYear ?? business.founded_year ?? '',
    email: business.email || '',
    gstNumber: business.gstNumber || business.gst_number || '',
  };
};

const mergeBusinessData = (baseBusiness = {}, incomingBusiness = {}) => {
  const mergedBusiness = { ...baseBusiness };

  Object.entries(incomingBusiness || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      mergedBusiness[key] = value;
    }
  });

  return mergedBusiness;
};

const normalizeAccessRole = (role) => {
  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole === 'owner' || normalizedRole === 'admin') {
    return 'admin';
  }
  if (normalizedRole === 'staff' || normalizedRole === 'viewer') {
    return 'staff';
  }
  return 'admin';
};

const isOwnerLikeAccessRole = (role) => {
  const normalizedRole = normalizeAccessRole(role);
  return normalizedRole === 'admin';
};

const isValidIdValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false;
    }

    const numericValue = Number(trimmedValue);
    if (!Number.isNaN(numericValue)) {
      return numericValue > 0;
    }

    return true;
  }

  return false;
};

const hasRealBusinessName = (name) => {
  const normalizedName = String(name || '').trim().toLowerCase();
  if (!normalizedName) {
    return false;
  }

  return normalizedName !== 'your business name' && normalizedName !== 'business hub';
};

const hasValidBusinessProfile = (business) => {
  const normalizedBusiness = normalizeBusinessData(business);
  if (!normalizedBusiness) {
    return false;
  }

  const businessName = normalizedBusiness.name || normalizedBusiness.companyName || '';
  const businessId = normalizedBusiness.id || normalizedBusiness.companyId || normalizedBusiness.company_id;

  return hasRealBusinessName(businessName) && isValidIdValue(businessId);
};

const hasLinkedCompany = (userData = {}) => {
  const businessId = userData?.business?.id || userData?.business?.companyId || userData?.companyId;
  return isValidIdValue(businessId);
};

const shouldRequireBusinessOnboarding = (currentUserType, userData = null) => {
  if (currentUserType !== 'business-owner' || !userData) {
    return false;
  }

  if (Boolean(userData.accessPending)) {
    return true;
  }

  if (Boolean(userData.requiresBusinessSetup)) {
    return true;
  }

  const accessRole = normalizeAccessRole(userData.accessRole);
  const isStaffUser = accessRole === 'staff' || Boolean(userData.isStaff);

  if (isStaffUser) {
    return !hasLinkedCompany(userData);
  }

  return !hasValidBusinessProfile(userData.business);
};

const isCustomerPortalEnabled = String(process.env.REACT_APP_ENABLE_CUSTOMER_PORTAL || 'false').toLowerCase() === 'true';

const restoreBusinessUserSession = (rawUserData = null) => {
  if (!rawUserData || typeof rawUserData !== 'object') {
    return null;
  }

  const restoredUserData = { ...rawUserData };
  let cachedBusinessData = null;
  const shouldReadBusinessCache = Boolean(
    restoredUserData.id &&
    !restoredUserData.requiresBusinessSetup &&
    (restoredUserData.business || isValidIdValue(restoredUserData.companyId))
  );

  if (shouldReadBusinessCache) {
    const cachedBusinessRaw = localStorage.getItem(`business_${restoredUserData.id}`);
    if (cachedBusinessRaw) {
      try {
        cachedBusinessData = normalizeBusinessData(JSON.parse(cachedBusinessRaw));
      } catch (cacheError) {
        console.warn('Unable to parse cached business session data:', cacheError);
      }
    }
  }

  const userBusinessData = normalizeBusinessData(restoredUserData.business);
  let mergedBusinessData = null;

  if (cachedBusinessData && userBusinessData) {
    mergedBusinessData = mergeBusinessData(cachedBusinessData, userBusinessData);
  } else {
    mergedBusinessData = cachedBusinessData || userBusinessData;
  }

  if (mergedBusinessData) {
    restoredUserData.business = mergedBusinessData;
  } else {
    delete restoredUserData.business;
  }

  if (!isValidIdValue(restoredUserData.companyId) && isValidIdValue(mergedBusinessData?.id)) {
    restoredUserData.companyId = mergedBusinessData.id;
  }

  restoredUserData.requiresBusinessSetup = shouldRequireBusinessOnboarding('business-owner', restoredUserData);

  return restoredUserData;
};

// Modern Colorful Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1', // Indigo instead of blue
      light: '#a5b4fc',
      dark: '#4338ca',
    },
    secondary: {
      main: '#f59e0b', // Amber
      light: '#fcd34d',
      dark: '#d97706',
    },
    success: {
      main: '#10b981', // Emerald
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b', // Amber
      light: '#fcd34d',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444', // Red
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
});

// Sidebar Component
function Sidebar({ user, onSignOut, userType }) {
  const location = useLocation();
  const accessRole = normalizeAccessRole(user?.accessRole);
  const hasOwnerLevelAccess = isOwnerLikeAccessRole(accessRole);

  const businessOwnerItems = [
    { label: 'Dashboard', path: '/', icon: '\uD83D\uDCCA' },
    { label: 'Dealers', path: '/dealers', icon: '\uD83E\uDD1D', ownerOnly: true },
    { label: 'Purchases', path: '/purchases', icon: '\uD83D\uDED2', ownerOnly: true },
    { label: 'Inventory', path: '/inventory', icon: '\uD83D\uDCE6' },
    { label: 'Sales', path: '/sales', icon: '\uD83D\uDCB0' },
    { label: 'Customers', path: '/customers', icon: '\uD83D\uDC65' },
    { label: 'Profit', path: '/profit', icon: '\uD83D\uDCC8', ownerOnly: true },
    { label: 'Expenses', path: '/expenses', icon: '\uD83D\uDCB8', ownerOnly: true },
    { label: 'Invoices', path: '/invoices', icon: '\uD83D\uDCC4' },
    { label: 'Reports', path: '/reports', icon: '\uD83D\uDCC5', ownerOnly: true },
    { label: 'Business Intelligence', path: '/business-intelligence', icon: '\uD83E\uDDE0', ownerOnly: true },
    { label: 'Data Import', path: '/data-import', icon: '\uD83D\uDCE5', ownerOnly: true },
    { label: 'Profile', path: '/profile', icon: '\uD83D\uDC64' },
    { label: 'Settings', path: '/settings', icon: '\u2699\uFE0F', ownerOnly: true },
    { label: 'E-Commerce', path: '/products', icon: '\uD83D\uDED2', ownerOnly: true },
  ];

  const customerItems = [
    { label: 'Find Stores', path: '/stores', icon: '\uD83C\uDFEA' },
    { label: 'Shop', path: '/products', icon: '\uD83D\uDECD\uFE0F' },
    { label: 'Cart', path: '/cart', icon: '\uD83D\uDED2' },
    { label: 'Orders', path: '/orders', icon: '\uD83D\uDCE6' },
    { label: 'Profile', path: '/profile', icon: '\uD83D\uDC64' },
  ];

  const navigationItems = userType === 'customer'
    ? customerItems
    : businessOwnerItems.filter(item => !item.ownerOnly || hasOwnerLevelAccess);

  const colors = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  ];

  return (
    <Box
      key={`sidebar-${user?.business?.name || 'default'}`}
      sx={{
        width: { xs: '100%', md: 340 },
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        position: { xs: 'relative', md: 'fixed' },
        left: 0,
        top: 0,
        zIndex: 1000,
        display: { xs: 'none', md: 'block' },
        boxShadow: '4px 0 20px rgba(0, 0, 0, 0.15)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Sidebar Content */}
      <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header Section - Compact */}
        <Box sx={{
          p: 2.5,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          {/* Logo/Icon and Business Name in one row */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              mr: 2,
              flexShrink: 0,
              overflow: 'hidden'
            }}>
              <img
                src="/logo.png"
                alt="Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: 'white',
                  display: 'none'
                }}
              >
                💼
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: '1.1rem',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  letterSpacing: '-0.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {(() => {
                  const businessName = user?.business?.name || 'Dhandhapaani';
                  
                  
                  return businessName;
                })()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}
              >
                Business Platform
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation Section */}
        <Box sx={{ flex: 1, px: 3, py: 3 }}>
          <Typography
            variant="overline"
            sx={{
              fontWeight: 700,
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 2,
              letterSpacing: '1px',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              display: 'block',
              textAlign: 'center'
            }}
          >
            Main Navigation
          </Typography>

          {/* Navigation Links */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            maxHeight: 'calc(100vh - 350px)',
            overflow: 'hidden',
          }}>
            {navigationItems.map((item, index) => {
              const isActive = location.pathname === item.path;

              return (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    color: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.8)',
                    background: isActive
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.8) 0%, rgba(168, 85, 247, 0.8) 100%)'
                      : 'transparent',
                    border: isActive
                      ? '1px solid rgba(255, 255, 255, 0.2)'
                      : '1px solid transparent',
                    borderRadius: 2,
                    py: 1.75,
                    px: 2.5,
                    fontWeight: isActive ? 700 : 500,
                    textTransform: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                      transition: 'left 0.5s',
                    },
                    '&:hover': {
                      background: isActive
                        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(168, 85, 247, 0.9) 100%)'
                        : 'rgba(255, 255, 255, 0.08)',
                      transform: 'translateX(8px)',
                      boxShadow: isActive
                        ? '0 8px 25px rgba(99, 102, 241, 0.3)'
                        : '0 4px 15px rgba(0, 0, 0, 0.1)',
                      '&::before': {
                        left: '100%',
                      }
                    },
                    '&:active': {
                      transform: 'translateX(4px) scale(0.98)',
                    }
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Typography sx={{
                      mr: 2.5,
                      fontSize: '1.3rem',
                      opacity: isActive ? 1 : 0.8,
                      transition: 'opacity 0.3s ease'
                    }}>
                      {item.icon}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.95rem',
                      fontWeight: isActive ? 600 : 500,
                      letterSpacing: '0.02em'
                    }}>
                      {item.label}
                    </Typography>
                    {isActive && (
                      <Box sx={{
                        position: 'absolute',
                        right: 8,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#ffffff',
                        boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)'
                      }} />
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* User Profile Section */}
        {user && (
          <Box sx={{
            p: 3,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)',
            backdropFilter: 'blur(10px)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <Avatar
                src={user.imageBase64 || localStorage.getItem(`profile_image_${user.id}`) || user.imageUrl || ''}
                alt={user.name}
                sx={{
                  width: 48,
                  height: 48,
                  mr: 2.5,
                  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                  color: 'white',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)'
                }}
                onError={(e) => {
                  

                  // Try to get base64 from localStorage if not already using it
                  const storedBase64 = localStorage.getItem(`profile_image_${user.id}`);
                  if (storedBase64 && e.target.src !== storedBase64) {
                    
                    e.target.src = storedBase64;
                    return;
                  }

                  // If Google profile image fails, set transparent fallback
                  if (user.imageUrl && e.target.src === user.imageUrl) {
                    
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+';
                  }
                }}
              >
                {/* Show user initials as fallback */}
                {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {user.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    fontWeight: 500
                  }}
                >
                  {user.business?.name || 'Business Owner'}
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={onSignOut}
              fullWidth
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 2,
                py: 1.25,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)',
                  borderColor: 'rgba(239, 68, 68, 0.5)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                }
              }}
            >
              🚪 Sign Out
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const requiresBusinessOnboarding = shouldRequireBusinessOnboarding(userType, user);

  // User activity tracking to prevent disruptive refreshes
  const [isUserActive, setIsUserActive] = useState(false);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // User activity detection
  const updateUserActivity = useCallback(() => {
    setIsUserActive(true);
    lastActivityRef.current = Date.now();

    // Store activity timestamp in localStorage for cross-component access
    localStorage.setItem('lastUserActivity', Date.now().toString());

    // Clear existing timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    // Set user as inactive after 5 minutes of no activity
    activityTimeoutRef.current = setTimeout(() => {
      setIsUserActive(false);
    }, 5 * 60 * 1000); // 5 minutes
  }, []);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => updateUserActivity();

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initial activity update
    updateUserActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [updateUserActivity]);

  useEffect(() => {
    // Get the selected user type first
    const storedUserType = localStorage.getItem('userType');

    if (storedUserType === 'business-owner') {
      // STRICTLY BUSINESS OWNER MODE - Only check business auth data
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');

      if (token && userData) {
        const parsedUserData = JSON.parse(userData);
        const mergedUserData = restoreBusinessUserSession(parsedUserData);

        if (mergedUserData) {
          localStorage.setItem('userData', JSON.stringify(mergedUserData));
          if (mergedUserData.id) {
            if (mergedUserData.business) {
              localStorage.setItem(`business_${mergedUserData.id}`, JSON.stringify(mergedUserData.business));
            } else {
              localStorage.removeItem(`business_${mergedUserData.id}`);
            }
          }

          setUser(mergedUserData);
          setUserType(storedUserType);
          setShowLandingPage(false);
        }

        // Access token setup removed - using database only
      }
      // If no business auth data, user remains null - ProtectedRoute will show Auth component

    } else if (storedUserType === 'customer') {
      // STRICTLY CUSTOMER MODE - Only check customer auth data
      const customerToken = localStorage.getItem('customerToken');
      const customerData = localStorage.getItem('customerData');

      if (customerToken && customerData) {
        const parsedCustomerData = JSON.parse(customerData);
        setUser(parsedCustomerData);
        setUserType(storedUserType);
        setShowLandingPage(false);
      }
      // If no customer auth data, user remains null - ProtectedRoute will show CustomerAuth component
    } else {
      // Fallback recovery: if userType is missing but auth data exists, restore session automatically
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const customerToken = localStorage.getItem('customerToken');
      const customerData = localStorage.getItem('customerData');

      if (token && userData) {
        const parsedUserData = JSON.parse(userData);
        const mergedUserData = restoreBusinessUserSession(parsedUserData);

        if (mergedUserData) {
          localStorage.setItem('userData', JSON.stringify(mergedUserData));
          if (mergedUserData.id) {
            if (mergedUserData.business) {
              localStorage.setItem(`business_${mergedUserData.id}`, JSON.stringify(mergedUserData.business));
            } else {
              localStorage.removeItem(`business_${mergedUserData.id}`);
            }
          }

          setUser(mergedUserData);
          setUserType('business-owner');
          localStorage.setItem('userType', 'business-owner');
          setShowLandingPage(false);
        }
      } else if (customerToken && customerData) {
        const parsedCustomerData = JSON.parse(customerData);
        setUser(parsedCustomerData);
        setUserType('customer');
        localStorage.setItem('userType', 'customer');
        setShowLandingPage(false);
      }
    }
    // If no userType selected, showLandingPage remains true

    setLoading(false);
  }, []);

  const handleAuthSuccess = (userData, businessData = null, authType = null) => {
    const isCustomerAuth = authType === 'customer' || userData?.userType === 'customer';
    let normalizedBusiness = !isCustomerAuth
      ? normalizeBusinessData(businessData || userData?.business)
      : null;

    if (!isCustomerAuth && userData?.id && normalizedBusiness) {
      const cachedBusiness = localStorage.getItem(`business_${userData.id}`);
      if (cachedBusiness) {
        try {
          const parsedCachedBusiness = normalizeBusinessData(JSON.parse(cachedBusiness));
          if (parsedCachedBusiness) {
            normalizedBusiness = mergeBusinessData(parsedCachedBusiness, normalizedBusiness);
          }
        } catch (cacheError) {
          console.warn('Unable to parse cached business data:', cacheError);
        }
      }
    }

    const mergedUserData = normalizedBusiness
      ? { ...userData, business: normalizedBusiness }
      : { ...userData };

    if (!isCustomerAuth) {
      mergedUserData.requiresBusinessSetup = shouldRequireBusinessOnboarding('business-owner', mergedUserData);
    }

    if (!isCustomerAuth && mergedUserData?.id) {
      localStorage.setItem('userData', JSON.stringify(mergedUserData));
      if (normalizedBusiness) {
        localStorage.setItem(`business_${mergedUserData.id}`, JSON.stringify(normalizedBusiness));
      } else {
        localStorage.removeItem(`business_${mergedUserData.id}`);
      }
    }

    setUser(mergedUserData);

    // Persist mode reliably to avoid role picker on refresh with valid auth session
    const resolvedAuthType = authType || (isCustomerAuth ? 'customer' : 'business-owner');
    setUserType(resolvedAuthType);
    localStorage.setItem('userType', resolvedAuthType);

    // Access token setup removed - using database only

    // Business sheet setup removed - using database only
  };

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setShowLandingPage(false);
    localStorage.setItem('userType', type);
  };

  const handleBackToRoleSelection = () => {
    localStorage.removeItem('userType');
    setUserType(null);
    setShowLandingPage(true);
  };
  // Business name sync removed - using database only

  const handleSignOut = async () => {
    try {
      // Get user type before clearing
      const currentUserType = localStorage.getItem('userType');

      // Clear all localStorage data
      localStorage.clear();

      // Reset user state
      setUser(null);

      // For customers, redirect to landing page instead of reloading
      if (currentUserType === 'customer') {
        setUserType(null);
        setShowLandingPage(true);
        console.log('Customer signed out, redirecting to landing page');
      } else {
        // For business owners, reload the page
        if (!isUserActive) {
          setTimeout(() => {
            window.location.reload();
          }, 100);
        } else {
          console.log('User is active, skipping page reload during sign-out');
        }
      }

    } catch (error) {
      // Force clear everything even if there's an error
      localStorage.clear();
      setUser(null);
      setUserType(null);
      setShowLandingPage(true);
    }
  };

  // Protected Route component
  const ProtectedRoute = ({ children }) => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <div>Loading...</div>
        </Box>
      );
    }

    // Special handling for checkout page - don't show landing page if user is navigating to checkout
    const isCheckoutPage = window.location.pathname === '/checkout';
    const hasModeParam = new URLSearchParams(window.location.search).has('mode');

    if (showLandingPage && !isCheckoutPage) {
      return <LandingPage onSelectType={handleUserTypeSelect} />;
    }

    if (!user) {
      // For checkout page with mode parameter, try to restore authentication first
      if (isCheckoutPage && hasModeParam) {
        const mode = new URLSearchParams(window.location.search).get('mode');
        if (mode === 'customer') {
          const customerToken = localStorage.getItem('customerToken');
          const customerData = localStorage.getItem('customerData');
          if (customerToken && customerData) {
            // Restore customer authentication
            const parsedCustomerData = JSON.parse(customerData);
            setUser(parsedCustomerData);
            setUserType('customer');
            return children; // Allow access to checkout
          }
        }
      }

      if (userType === 'customer' && !isCustomerPortalEnabled) {
        return (
          <CustomerPortalComingSoon
            mode="auth-entry"
            feature="Customer login and signup"
            onSwitchAccount={handleBackToRoleSelection}
          />
        );
      }

      return userType === 'customer' ? (
        <CustomerAuth onAuthSuccess={handleAuthSuccess} />
      ) : (
        <Auth onAuthSuccess={handleAuthSuccess} />
      );
    }

    if (!userType) {
      return <UserTypeSelection onSelectType={handleUserTypeSelect} />;
    }

    if (requiresBusinessOnboarding) {
      return <Auth onAuthSuccess={handleAuthSuccess} />;
    }

    return children;
  };

  const BusinessAccessRoute = ({ children, ownerOnly = false }) => {
    if (userType === 'customer') {
      return <Navigate to="/" replace />;
    }

    if (!ownerOnly) {
      return children;
    }

    const accessRole = normalizeAccessRole(user?.accessRole);
    if (isOwnerLikeAccessRole(accessRole)) {
      return children;
    }

    return <Navigate to="/" replace />;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          {/* Sidebar - only show for authenticated users */}
          {user && !requiresBusinessOnboarding && (
            <Sidebar user={user} onSignOut={handleSignOut} userType={userType} />
          )}

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              ml: { xs: 0, md: user && !requiresBusinessOnboarding ? '320px' : 0 },
              minHeight: '100vh',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            }}
          >
            {/* Navbar - only show for authenticated users */}
            {user && !requiresBusinessOnboarding && <Navbar user={user} onSignOut={handleSignOut} />}
            <Box sx={{ p: 3 }}>
              <Routes>
                {/* Public routes - no authentication required */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/database-test" element={<DatabaseTest />} />

                {/* Protected routes - require authentication */}
                <Route path="/stores" element={
                  <ProtectedRoute>
                    <StoreLocator />
                  </ProtectedRoute>
                } />
                <Route path="/products" element={
                  <ProtectedRoute>
                    {userType === 'customer' ? <ProductCatalog /> : <EcommerceInterest user={user} />}
                  </ProtectedRoute>
                } />
                <Route path="/cart" element={
                  <ProtectedRoute>
                    <ShoppingCart />
                  </ProtectedRoute>
                } />
                <Route path="/checkout" element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                } />
                <Route path="/points" element={
                  <ProtectedRoute>
                    <PointsHistory />
                  </ProtectedRoute>
                } />
                <Route path="/orders" element={
                  <ProtectedRoute>
                    <Orders />
                  </ProtectedRoute>
                } />
                <Route path="/" element={
                  <ProtectedRoute>
                    {userType === 'customer' ? <CustomerDashboard /> : <Dashboard />}
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <ReportsOverview />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    {userType === 'customer' ? <CustomerProfile /> : <Profile user={user} onUpdate={setUser} />}
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <Settings user={user} onUpdate={setUser} />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/dealers" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <Dealers />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/purchases" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <Purchases />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/sales" element={
                  <ProtectedRoute>
                    <Sales />
                  </ProtectedRoute>
                } />
                <Route path="/inventory" element={
                  <ProtectedRoute>
                    <Inventory />
                  </ProtectedRoute>
                } />
                <Route path="/customers" element={
                  <ProtectedRoute>
                    <Customers />
                  </ProtectedRoute>
                } />
                <Route path="/profit" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <Profit />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/expenses" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <Expenses />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/invoices" element={
                  <ProtectedRoute>
                    <Invoices />
                  </ProtectedRoute>
                } />
                <Route path="/business-intelligence" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <BusinessIntelligence />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
                <Route path="/data-import" element={
                  <ProtectedRoute>
                    <BusinessAccessRoute ownerOnly>
                      <DataImport />
                    </BusinessAccessRoute>
                  </ProtectedRoute>
                } />
              </Routes>
            </Box>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;



